import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_ENDPOINT } from "../../utilities/constant";
import { isValidEmail, toastError, toastSuccess } from "../../utilities/utils";
import Cookies from "js-cookie";
import { createPortal } from "react-dom";

const FeatureRequestPopup = ({ isOpen, onClose }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState(Cookies.get("email") || "");
  const [featureRequest, setFeatureRequest] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // State for portal mounting
  const [mounted, setMounted] = useState(false);
  
  // Setup effect for mounting
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  
  useEffect(() => {
    // Try to get the user's profile data for pre-filling the form
    const token = Cookies.get("token");
    if (token && isOpen) {
      const fetchUserDetails = async () => {
        try {
          const response = await axios.get(`${API_ENDPOINT}/userdetails/`, {
            headers: {
              Authorization: `Token ${token}`,
            },
          });
          
          const userData = response.data.data;
          if (userData?.user?.first_name) {
            setName(userData.user.first_name.charAt(0).toUpperCase() + userData.user.first_name.slice(1));
          }
          
          if (userData?.user?.email) {
            setEmail(userData.user.email);
          }
        } catch (err) {
          console.log("Could not fetch user details for pre-filling form", err);
        }
      };
      
      fetchUserDetails();
    }
  }, [isOpen]);

  // Add effect to prevent body scrolling when popup is open
  useEffect(() => {
    if (isOpen) {
      // Save the current overflow style
      const originalOverflow = document.body.style.overflow;
      // Prevent scrolling on the body
      document.body.style.overflow = 'hidden';
      
      // Restore original overflow on cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name) {
      toastError("Name is required");
      return;
    }
    if (!email) {
      toastError("Email is required");
      return;
    }
    if (!isValidEmail(email)) {
      toastError("Email is invalid");
      return;
    }
    if (!featureRequest) {
      toastError("Feature request details are required");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API_ENDPOINT}/contact/`, {
        name,
        email,
        company: "",
        isCustomer: null,
        message: featureRequest,
        type: "Feature Request"
      });

      if (response.status === 200) {
        setName("");
        setFeatureRequest("");
        toastSuccess("Thanks for your feature request! We will review it and get back to you soon.");
        onClose(); // Close the popup after successful submission
      } else {
        toastError("Failed to submit feature request");
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error:", error.message);
      toastError(
        "An error occurred while submitting the feature request. Please try again later."
      );
    }
  };

  if (!isOpen) return null;

  const popupContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={(e) => {
        // Close the popup if the click is directly on the background (not on the content)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative w-[95%] max-h-[90vh] overflow-y-auto max-w-lg mx-auto bg-white rounded-lg shadow-2xl">
        {/* Close button */}
        <div
          className="absolute top-4 right-4 cursor-pointer z-50"
          onClick={onClose}
          style={{
            display: "flex",
            width: "31px",
            height: "30px",
            padding: "4px",
            borderRadius: "4px",
            background: "rgba(255, 255, 255, 0.2)",
            boxShadow: "0px 2px 10px 0px rgba(20, 64, 175, 0.15)",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease-in-out",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(1px)";
            e.currentTarget.style.boxShadow = "0px 1px 5px rgba(20, 64, 175, 0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0px 2px 10px rgba(20, 64, 175, 0.15)";
          }}
        >
          <span
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#FFFFFF",
              lineHeight: "20px",
            }}
          >
            &times;
          </span>
        </div>

        {/* Header with decorative elements */}
        <div className="bg-gradient-to-r from-[#049A55] to-[#037346] text-white p-4 md:p-6 relative overflow-hidden">
          {/* Decorative pattern */}
          <div className="absolute inset-0 opacity-5">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                  <circle cx="3" cy="3" r="1.5" fill="white" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#dots)" />
            </svg>
          </div>
          
          <div className="text-center relative z-10">
            <div className="inline-flex items-center justify-center space-x-2 bg-white/20 text-white text-xs md:text-sm font-medium rounded-full px-3 py-1 md:px-4 md:py-1.5 mb-3 md:mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <span>Submit a Feature Request</span>
            </div>
            <h2 className="text-xl md:text-2xl font-bold mb-1 md:mb-2">
              Help Shape SERPtag's Future
            </h2>
            <p className="text-white/80 text-xs md:text-sm">
              Your ideas directly influence our product roadmap
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="p-4 md:p-6">
          <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
            {/* Name field */}
            <div className="space-y-1 md:space-y-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 md:py-2.5 text-sm rounded-md border border-gray-200 focus:border-[#049A55] focus:ring-2 focus:ring-[#049A55]/20 transition-all bg-white"
                  placeholder="Your full name"
                  required
                />
              </div>
            </div>

            {/* Email field */}
            <div className="space-y-1 md:space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 md:py-2.5 text-sm rounded-md border border-gray-200 focus:border-[#049A55] focus:ring-2 focus:ring-[#049A55]/20 transition-all bg-white"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            {/* Feature Request field */}
            <div className="space-y-1 md:space-y-2">
              <label htmlFor="feature-request" className="block text-sm font-medium text-gray-700">
                Feature Request <span className="text-red-500">*</span>
              </label>
              <div className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3">
                Please describe the feature you'd like to see, how it would help your workflow, and any specific details about how it should work.
              </div>
              <div className="relative">
                <textarea
                  id="feature-request"
                  value={featureRequest}
                  onChange={(e) => setFeatureRequest(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 text-sm rounded-md border border-gray-200 focus:border-[#049A55] focus:ring-2 focus:ring-[#049A55]/20 transition-all bg-white"
                  placeholder="Describe the feature you would like to see"
                  rows="4"
                  required
                />
              </div>
            </div>

            {/* Submit button with animation */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 md:py-3 px-4 rounded-md bg-[#049A55] text-white font-medium hover:bg-[#049A55]/90 focus:outline-none focus:ring-4 focus:ring-[#049A55]/30 transition-all transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-70 disabled:hover:translate-y-0 relative overflow-hidden group"
            >
              <div className="absolute inset-0 w-3 bg-white/20 skew-x-[45deg] group-hover:transition-all group-hover:duration-1000 -left-20 group-hover:left-full"></div>
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center justify-center text-sm md:text-base">
                  Submit Feature Request
                  <svg className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  // Return null if component is not mounted yet or use portal to render at top level
  return mounted && typeof window !== 'undefined' 
    ? createPortal(popupContent, document.body) 
    : null;
};

export default FeatureRequestPopup; 